import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { use100vh } from 'react-div-100vh'
import {
  Box,
  ButtonUnstyled,
  Icon,
  P,
  Button,
  Avatar,
  Collapse,
} from '@allied-solutions/affinity'
import { LinkAnchor } from './LinkAnchor'
import { useStore } from '../hooks/useStore'
import { navigate } from 'gatsby'
import { theme } from '../theme'
import { Logo } from './Logo'
import { clearCiamSessionAuth } from '../hooks/useCiamAuthorize'
import { useCiamLogout } from '../hooks/useCiamLogout'
import {
  Close01,
  Home01,
  QuestionCircle,
  UserSolid,
  LogOut01,
} from '@allied-solutions/affinity-react-icons/dist/standard'

const StyledButton = styled(Button)`
  width: 100%;
  margin-bottom: ${props => props.theme.space['2xl']};
`

export const MobileUserMenu = ({ needMenu, setNeedMenu, menuHeight }) => {
  const { dispatch, state } = useStore()
  const { handleLogout: handleCiamLogout } = useCiamLogout()
  const menuTriggerRef = useRef(null)
  const viewportHeight = use100vh()
  const maxMenuHeightPx =
    typeof menuHeight === 'number'
      ? menuHeight
      : Math.max(320, viewportHeight - 48)

  useEffect(() => {
    document.body.style.overflow = needMenu ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [needMenu])

  const handleLogout = async () => {
    if (typeof window === 'undefined') {
      return
    }

    const signOffSuccessful = await handleCiamLogout()
    if (!signOffSuccessful) {
      dispatch({
        type: 'addToState',
        payload: { hasSignoutError: true },
      })
      return
    }

    clearCiamSessionAuth()
    dispatch({
      type: 'addToState',
      payload: { TriggeredLogout: true, userData: null, ciamUserId: null },
    })
    setTimeout(() => navigate('/'), 0)
  }

  return (
    <>
      <Box display={needMenu ? 'none' : 'contents'}>
        <ButtonUnstyled
          ref={menuTriggerRef}
          id="homeSupportMenuToggle"
          onClick={() => setNeedMenu(true)}
        >
          <Avatar gradient="teal" size="avatars.lg">
            {state?.userData?.Email?.charAt(0).toUpperCase()}
          </Avatar>
        </ButtonUnstyled>
      </Box>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex="dialog"
        css={`
          pointer-events: ${needMenu ? 'auto' : 'none'};
        `}
      >
        <Collapse
          id="Global__Header__Collapse--userMenu"
          isOpen={needMenu}
          triggerRef={menuTriggerRef}
        >
          <Box pb="6xl" pt={0}>
            <Box
              width="100%"
              pt={6}
              pb={16}
              bg="white"
              overflow="auto"
              maxHeight={`${maxMenuHeightPx}px`}
              borderRadius={theme.radii.xl}
              boxShadow="xl"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid"
                borderColor="mutedLine"
                px={8}
                pb={8}
              >
                <Logo />
                <ButtonUnstyled
                  onClick={() => setNeedMenu(false)}
                  id="homeSupportMenuToggleClose"
                  aria-label="close menu"
                >
                  <Box
                    width="8"
                    height="8"
                    borderRadius="8"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gradient="blue"
                  >
                    <Icon src={Close01} color="white" />
                  </Box>
                </ButtonUnstyled>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap="sm"
                borderBottom="1px solid"
                borderColor="mutedLine"
                py={4}
                width="100%"
                mb={8}
                px={8}
              >
                <Avatar gradient="teal" size="avatars.lg">
                  {state?.userData?.Email?.charAt(0).toUpperCase()}
                </Avatar>
                <Box display="flex" flexDirection="column" gap={0}>
                  <Box display="flex" alignItems="center">
                    <P typeStyle="labelSmall.bold" mb="0">
                      {state?.userData?.Name?.['Given']
                        ? state?.userData?.Name?.['Given']
                        : ''}
                    </P>
                    <P typeStyle="labelSmall.bold" mb="0">
                      {state?.userData?.Name?.['Family']
                        ? state?.userData?.Name?.['Family']
                        : ''}
                    </P>
                  </Box>
                  {state?.userData?.Email}
                </Box>
              </Box>
              <Box px={8}>
                <LinkAnchor
                  to="/dashboard/"
                  id="Global__Header__Link--dashboard"
                  onClick={() => setNeedMenu(false)}
                >
                  <StyledButton
                    id="dashboard-button"
                    icon={Home01}
                    iconPos="left"
                    outlined
                    variant="gray"
                    size="lg"
                  >
                    Dashboard
                  </StyledButton>
                </LinkAnchor>
                <LinkAnchor
                  to="/profile/"
                  id="Global__Header__Link--profile"
                  onClick={() => setNeedMenu(false)}
                >
                  <StyledButton
                    id="profile-button"
                    icon={UserSolid}
                    iconPos="left"
                    outlined
                    variant="gray"
                    size="lg"
                  >
                    My Profile
                  </StyledButton>
                </LinkAnchor>
                <LinkAnchor
                  to="/support/"
                  id="Global__Header__Link--support"
                  onClick={() => setNeedMenu(false)}
                >
                  <StyledButton
                    id="home-support-button"
                    icon={QuestionCircle}
                    iconPos="left"
                    outlined
                    variant="gray"
                    size="lg"
                  >
                    Support
                  </StyledButton>
                </LinkAnchor>
                <StyledButton
                  id="Global__Header__Button--logout"
                  icon={LogOut01}
                  iconPos="left"
                  outlined
                  variant="gray"
                  size="lg"
                  onClick={handleLogout}
                >
                  Logout
                </StyledButton>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </>
  )
}
